/**
 * The current view, as a CSV an operator can open in a spreadsheet.
 *
 * **It exports what is ON SCREEN, and nothing more.** Every value it writes has already been served
 * to this browser and rendered in front of the person pressing the button, so the export discloses
 * nothing new and needs no second authorization. That is a property of this function rather than an
 * assumption about its caller: it takes rendered ROWS, not ids to fetch.
 *
 * **A value is quoted whenever it could be misread, and never interpreted.** A leading `=`, `+`,
 * `-` or `@` is what a spreadsheet treats as a formula, and a display name is operator-entered text
 * — so a cell beginning with one is prefixed with a single quote before it is written. That is the
 * standard defence against CSV injection, and the reason it belongs here rather than in the
 * component is that it is the kind of rule somebody removes while "simplifying" a string join.
 *
 * **CRLF and a UTF-8 byte-order mark.** Both are for the spreadsheet on the operator's own machine:
 * without the mark, Excel on Windows reads `Martínez` as `MartÃ­nez`, and it is the one product in
 * this workflow nobody gets to choose.
 */

/** The characters that make a spreadsheet treat a cell as a formula rather than as text. */
const FORMULA_STARTERS = new Set(['=', '+', '-', '@', '\t', '\r']);

/** One cell, escaped for CSV and defused for a spreadsheet. */
export function csvCell(value: string): string {
  const defused = FORMULA_STARTERS.has(value.charAt(0)) ? `'${value}` : value;
  return `"${defused.replaceAll('"', '""')}"`;
}

/** The UTF-8 byte-order mark, so a spreadsheet reads accented names correctly. */
export const CSV_BOM = '﻿';

/** A whole file: a header row and one row per record, CRLF-separated. */
export function toCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [headers, ...rows].map((cells) => cells.map(csvCell).join(','));
  return `${CSV_BOM}${lines.join('\r\n')}\r\n`;
}

/** A file name that sorts chronologically and never collides on a busy day. */
export function csvFileName(prefix: string, instant: Date, timeZone: string): string {
  // **Cut in the installation's zone, like every other instant this product renders.** `toISOString`
  // is UTC, so an export taken at 00:30 in Madrid was named with the previous day — the one case
  // where a file name is the only record of when something was taken. `sv-SE` renders ISO order
  // natively, which is what makes the stamp sortable.
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(instant);
  return `${prefix}-${parts.replaceAll(/[: ]/gu, '-')}.csv`;
}
